import { useState } from "react";
import type { CanonicalWork } from "../generated/contracts";
import { contextualArtwork } from "../artwork";

function hash(value: string) {
  let number = 0;
  for (const character of value)
    number = ((number << 5) - number + character.charCodeAt(0)) | 0;
  return Math.abs(number);
}

type CoverWork = Pick<
  CanonicalWork,
  "id" | "title" | "kind" | "artworkUrl" | "series"
> &
  Partial<
    Pick<CanonicalWork, "topics" | "contributors" | "scriptureRefs" | "year">
  >;

export function Cover({
  work,
  size = "card",
  artworkOverride,
}: {
  work: CoverWork;
  size?: "card" | "hero" | "mini";
  artworkOverride?: string;
}) {
  const [failed, setFailed] = useState<string[]>([]);
  const seed = hash(work.series?.id || work.id);
  const sky = ["#54B8E7", "#79C9EE", "#A7DEF5", "#3FA9DC"][seed % 4];
  const initials = work.title
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 3)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
  const artwork =
    artworkOverride ||
    contextualArtwork({
      ...work,
      topics: work.topics || [],
      contributors: work.contributors || [],
      scriptureRefs: work.scriptureRefs || [],
    });
  if (artwork && !failed.includes(artwork))
    return (
      <div
        className={`cover cover--${size} cover--contextual`}
        aria-hidden="true"
      >
        <img
          src={artwork}
          onError={() => setFailed((items) => [...items, artwork])}
          alt=""
          loading={size === "hero" ? "eager" : "lazy"}
          decoding="async"
        />
      </div>
    );
  return (
    <div
      className={`cover cover--${size}`}
      style={
        {
          "--cover-sky": sky,
          "--cover-turn": `${(seed % 9) - 4}deg`,
        } as React.CSSProperties
      }
      aria-hidden="true"
    >
      <div className="cover__grain" />
      <div className="cover__rule" />
      <span className="cover__horizon">
        <i />
      </span>
      <span className="cover__initials">{initials}</span>
      <span className="cover__kind">
        {work.series?.title || work.kind.replaceAll("-", " ")}
      </span>
    </div>
  );
}
